import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Chip from "../../../ui/feedback/Chip";
import { classNames } from "../../../ui/styles/classNames";
import { termName, type TermCards } from "../../ontology/terms/termNames";
import { mostSpecificClasses, nestSaid, type Individual, type NestedDescription, type StatementCardShape, type StatementLayout, type StatementValue } from "./aboxViewModel";
import { statementCard, type StatementCard } from "./statementCard";
import TermLink from "./TermLink";

interface StatementSummaryProps {
  /** The statement as its view lays it out (`layOutStatement`). */
  readonly layout: StatementLayout;
  /** What the agent wrote about it; `""` for nothing. */
  readonly note: string;
  /** The commit of the data model version it was written against, where its terms open. */
  readonly modelCommit: string;
  /** The terms of that version in the statement's own project, which name them; `null` names them by their IRIs' ends. */
  readonly cards: TermCards | null;
  /** Its number in reading order, which its words in the text also carry; `null` for none. */
  readonly number: number | null;
  /** How the project's profile reads one statement (Daniel's card); `null` shows it as written. */
  readonly shape: StatementCardShape | null;
}

/**
 * One statement as a historian reads it (F94, O-1): the class of what it is about and its name,
 * then one row per property under the data model's label. Its words are the text's, shown there,
 * not quoted again; a term opens on the Data model beside (F75). The evidence that anchors it in
 * the text, such as its mentions and attestations, follows closed (AB-5), with the note when the
 * statement itself only names things.
 */
export default function StatementSummary({ layout, note, modelCommit, cards, number, shape }: StatementSummaryProps) {
  const { t } = useTranslation();
  // What it says, as one whole: each part under the value that names it (`nestSaid`).
  const [main, ...others] = nestSaid(layout.said);
  const card = statementCard(main, shape);
  const numbered = number === null ? null : <b className="app-title tabular-nums">#{number}</b>;
  const separated = (description: NestedDescription) => (
    <div key={description.individual.iri} className="border-t border-[var(--app-border)] pt-1">
      <Described description={description} cards={cards} commit={modelCommit} />
    </div>
  );
  const noteText = note === "" ? null : <p className="app-text-muted text-xs">{note}</p>;
  // A note on what a statement says shows with it; on one that only names things, it is about the evidence.
  const noteWithEvidence = layout.evidence.length > 0 && layout.said.every((description) => description.rows.length === 0);
  const evidence = <>{layout.evidence.map(separated)}{noteWithEvidence ? noteText : null}</>;
  return (
    <div className="space-y-1">
      {card !== null ? <Card card={card} number={numbered} cards={cards} commit={modelCommit} participation={shape?.participation?.property ?? null} />
        : main === undefined ? null : <Described description={main} cards={cards} commit={modelCommit} number={numbered} />}
      {others.map(separated)}
      {noteWithEvidence ? null : noteText}
      {layout.evidence.length === 0 ? null : main === undefined ? (
        // A line that is only evidence sits in its part's one evidence fold (PartStatements): shown as it is, no label.
        <div className="space-y-1 text-xs">{evidence}</div>
      ) : (
        <details className="space-y-1 text-xs">
          <summary className="app-text-muted w-fit cursor-pointer">{t("data.abox.evidence")}</summary>
          {evidence}
        </details>
      )}
    </div>
  );
}

interface Named {
  readonly cards: TermCards | null;
  readonly commit: string;
}

/**
 * What the text calls it: its label, else the words that say it (Daniel's surface form). Those
 * words are often the whole passage beside it, so they take one line, the rest on hover.
 */
function NameOf({ individual, className }: { individual: Individual; className?: string }) {
  if (individual.name !== null) return <span className={className}>{individual.name}</span>;
  if (individual.words === null) return null;
  return <span className={classNames("inline-block max-w-full truncate align-bottom", className)} title={individual.words}>{individual.words}</span>;
}

/** Its most specific classes, by the data model's parents; all of them until those load. */
function kindsOf(individual: Individual, cards: TermCards | null): string[] {
  return mostSpecificClasses(individual.classes, (iri) => cards?.get(iri)?.parents ?? []);
}

/**
 * Daniel's statement card: its number and kind, the class of what it is about; who takes part in
 * which role, one line each; then the rest it says under the data model's labels.
 */
function Card({ card, number, participation, cards, commit }: Named & { card: StatementCard; number: ReactNode; participation: string | null }) {
  const { t } = useTranslation();
  return (
    <>
      <p className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
        {number}
        {kindsOf(card.about.individual, cards).map((iri) => <Chip key={iri}><TermLink iri={iri} commit={commit} cards={cards} /></Chip>)}
        {card.about.individual.name === null ? null : <NameOf individual={card.about.individual} className="app-title font-semibold" />}
      </p>
      <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-0.5">
        {participation === null || card.participants.length === 0 ? null : (
          <div className="contents">
            <dt className="app-text-muted"><TermLink iri={participation} commit={commit} cards={cards} /></dt>
            <dd className="min-w-0">
              {card.participants.map((participant, index) => (
                <div key={index}>
                  {participant.actor === null ? "?" : <Value value={participant.actor} cards={cards} commit={commit} />}
                  {participant.role === null ? null : <><span className="app-text-muted"> {t("data.abox.asRole")} </span><Value value={participant.role} cards={cards} commit={commit} /></>}
                </div>
              ))}
            </dd>
          </div>
        )}
        <Rows rows={card.rows} cards={cards} commit={commit} />
      </dl>
    </>
  );
}

function Described({ description, cards, commit, number = null }: Named & { description: NestedDescription; number?: ReactNode }) {
  const { individual, rows } = description;
  // Something described by its properties is named only by a label: its words are the text beside it.
  const named = rows.length === 0 || individual.name !== null;
  return (
    <>
      <p className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
        {number}
        {kindsOf(individual, cards).map((iri) => <Chip key={iri}><TermLink iri={iri} commit={commit} cards={cards} /></Chip>)}
        {named ? <NameOf individual={individual} className="app-title font-semibold" /> : null}
      </p>
      {rows.length === 0 ? null : (
        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-0.5">
          <Rows rows={rows} cards={cards} commit={commit} />
        </dl>
      )}
    </>
  );
}

function Rows({ rows, cards, commit }: Named & { rows: NestedDescription["rows"] }) {
  return rows.map((row) => (
    <div key={row.property} className="contents">
      <dt className="app-text-muted"><TermLink iri={row.property} commit={commit} cards={cards} /></dt>
      <dd className="min-w-0">
        {row.values.map((value, index) => (value.kind === "nested" ? (
          // A part this statement describes reads under the value that names it.
          <div key={index} className="border-l-2 border-[var(--app-border)] pl-2">
            <Described description={value.description} cards={cards} commit={commit} />
          </div>
        ) : <span key={index}>{index === 0 ? null : ", "}<Value value={value} cards={cards} commit={commit} /></span>))}
      </dd>
    </div>
  ));
}

function Value({ value, cards, commit }: Named & { value: StatementValue | { readonly kind: "nested"; readonly description: NestedDescription } }) {
  const { t, i18n } = useTranslation();
  switch (value.kind) {
    case "text":
      return <>{value.text}</>;
    case "term":
      return <TermLink iri={value.iri} commit={commit} cards={cards} />;
    case "individual":
    case "nested": {
      // A participant described in the same statement is named in its line, like any other.
      const individual = value.kind === "nested" ? value.description.individual : value.individual;
      const kind = kindsOf(individual, cards).map((iri) => termName(iri, cards, i18n.resolvedLanguage ?? "en")).join(", ");
      if (individual.name === null && individual.words === null) return <>{kind === "" ? t("sources.document.regest.unnamed") : kind}</>;
      return <><NameOf individual={individual} />{kind === "" ? null : <span className="app-text-muted text-xs"> {kind}</span>}</>;
    }
  }
}
